import { resolveLiveToken } from "../lib/sessionToken";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { API_BASE_URL } from "../lib/api";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function Ayuda123Widget() {
  const { token, user } = useAuth();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const scrollHidden = useHideWhileScrolling(!open);
  const fabRef = useRef<HTMLButtonElement>(null);
  const placement = useFabDodge(fabRef, !open, location.pathname);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasOpenedRef = useRef(false);

  /* -- derive context from current page --------------------------------- */
  const userRoles = user?.roles ?? [];
  const userRole = userRoles.includes("superadmin")
    ? "superadmin"
    : userRoles.includes("admin")
      ? "admin"
      : "jugador";
  const contextPage = location.pathname;

  /* -- welcome message on first open ----------------------------------- */
  useEffect(() => {
    if (open && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      const welcome =
        userRole === "admin" || userRole === "superadmin"
          ? "Hola, soy Roger 🎾. Me sé Torneos123 de memoria: datos del torneo, convocatoria, inscripciones, formato, grupos, rondas, calendario, resultados y PDFs. Y si encuentras un error, dímelo y levanto un ticket de soporte. ¿En qué te ayudo?"
          : "Hola, soy Roger 🎾. Te puedo ayudar a consultar tus torneos, próximos partidos, resultados, ranking, reglamento y comunicados. ¿Qué quieres revisar?";
      setMessages([{ role: "assistant", content: welcome }]);
    }
  }, [open, userRole]);

  /* -- scroll to bottom when messages change --------------------------- */
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  /* -- focus input when panel opens ------------------------------------ */
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  /* -- send message ---------------------------------------------------- */
  const send = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading || !token) return;

    setInput("");
    setError(null);
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);

    try {
      const resp = await fetch(`${API_BASE_URL}/ayuda123/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${resolveLiveToken(token) ?? token}`,
        },
        body: JSON.stringify({
          message: text,
          context_page: contextPage,
          // Últimos turnos para que Roger siga el hilo (el server los recorta).
          history: messages.slice(-10).map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply },
      ]);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Error al contactar a Roger";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [input, loading, token, contextPage, messages]);

  /* -- keyboard --------------------------------------------------------- */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const quickPrompts =
    userRole === "admin" || userRole === "superadmin"
      ? ["Crear torneo", "Agregar jugadores", "Calendarizar", "Capturar resultado", "Reportar un problema"]
      : ["Mi próximo partido", "Mis resultados", "Mi ranking", "Mis torneos"];

  const sendQuickPrompt = (text: string) => {
    if (loading) return;
    void send(text);
  };

  /* -- render ----------------------------------------------------------- */
  return (
    <>
      {/* Floating toggle button */}
      {!open && (
        <button
          ref={fabRef}
          type="button"
          className={`ayuda123-fab${scrollHidden.hidden ? " ayuda123-fab--scroll-hidden" : ""}${placement.tab ? " ayuda123-fab--edge-tab" : ""}`}
          style={placement.lift ? ({ "--ayuda-fab-lift": `${placement.lift}px` } as React.CSSProperties) : undefined}
          data-placement={placement.tab ? "tab" : placement.lift ? "lifted" : "docked"}
          onClick={() => setOpen(true)}
          onFocus={scrollHidden.reveal}
          aria-label="Abrir Roger"
          title="Roger — Asistente de Torneos123"
        >
          💬
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="ayuda123-panel">
          <div className="ayuda123-header">
            <div className="ayuda123-header__copy">
              <span>🎾 Roger</span>
              <span className="ayuda123-header__sub">Asistente de Torneos123</span>
            </div>
            <button
              type="button"
              className="ayuda123-close-btn"
              onClick={() => setOpen(false)}
              aria-label="Cerrar Roger"
              title="Cerrar Roger"
            >
              ✕
            </button>
          </div>

          <div className="ayuda123-messages" ref={scrollRef}>
            {messages.map((m, i) => (
              <div
                key={i}
                className={`ayuda123-msg ${
                  m.role === "user" ? "ayuda123-msg--user" : "ayuda123-msg--bot"
                }`}
              >
                <div className="ayuda123-msg__bubble">{m.content}</div>
              </div>
            ))}

            {loading && (
              <div className="ayuda123-msg ayuda123-msg--bot">
                <div className="ayuda123-msg__bubble ayuda123-typing">
                  <span className="ayuda123-dot" />
                  <span className="ayuda123-dot" />
                  <span className="ayuda123-dot" />
                </div>
              </div>
            )}

            {error && (
              <div className="ayuda123-msg ayuda123-msg--bot">
                <div className="ayuda123-msg__bubble ayuda123-error">
                  {error}
                </div>
              </div>
            )}
          </div>

          <div className="ayuda123-quick-prompts">
            {quickPrompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => sendQuickPrompt(prompt)}
                disabled={loading || !token}
              >
                {prompt}
              </button>
            ))}
          </div>

          <div className="ayuda123-input-row">
            <input
              ref={inputRef}
              type="text"
              className="ayuda123-input"
              placeholder="Escribe tu pregunta..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading || !token}
              maxLength={2000}
            />
            <button
              type="button"
              className="ayuda123-send-btn"
              onClick={() => void send()}
              disabled={loading || !token || !input.trim()}
              aria-label="Enviar"
            >
              {loading ? "…" : "➤"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** En teléfonos (≤760px) la burbuja se esconde mientras el usuario hace
 *  scroll y reaparece ~600ms después de que se detiene. En escritorio no
 *  hace nada. Nunca se esconde si `enabled` es falso (panel abierto). */
export const AYUDA_FAB_SCROLL_IDLE_MS = 600;
export const AYUDA_FAB_MOBILE_QUERY = "(max-width: 760px)";

export function useHideWhileScrolling(enabled: boolean) {
  const [hidden, setHidden] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) {
      setHidden(false);
      return;
    }
    const isPhone = () =>
      typeof window.matchMedia === "function" &&
      window.matchMedia(AYUDA_FAB_MOBILE_QUERY).matches;
    const onScroll = () => {
      if (!isPhone()) return;
      setHidden(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setHidden(false);
      }, AYUDA_FAB_SCROLL_IDLE_MS);
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [enabled]);

  const reveal = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setHidden(false);
  }, []);

  return { hidden, reveal };
}

/* ------------------------------------------------------------------ */
/* Esquivar controles (teléfono)                                       */
/* ------------------------------------------------------------------ */

/** Lo que la burbuja nunca debe tapar estando quieta: botones, ligas,
 *  campos y renglones de leyenda (p. ej. «Bloqueada» en Club · Hoy). */
export const AYUDA_FAB_OBSTACLES = [
  "button",
  "a[href]",
  "input:not([type=hidden])",
  "select",
  "textarea",
  "summary",
  "[role=button]",
  "[role=link]",
  "[role=tab]",
  "[role=checkbox]",
  "[tabindex]:not([tabindex='-1'])",
  "[class*='legend'] > *",
  "[data-tap]",
  "[onclick]",
].join(",");

/** Candidatos a «se puede tocar» por cursor:pointer (tarjetas y renglones
 *  con manejador de clic que no son <button>). Solo se mide el estilo de
 *  los que caen en la franja derecha donde vive la burbuja. */
export const AYUDA_FAB_POINTER_CANDIDATES = "div,li,tr,td,article,label,section,span";

/** Tamaño del modo «pestaña» pegada al borde derecho (px). */
export const AYUDA_FAB_TAB = { width: 26, height: 44 };

export interface FabRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface FabPlacement {
  lift: number;
  tab: boolean;
}

const GAP = 4;

function hits(a: FabRect, b: FabRect, gapX = GAP) {
  return (
    a.left < b.right + gapX &&
    a.right > b.left - gapX &&
    a.top < b.bottom + GAP &&
    a.bottom > b.top - GAP
  );
}

function freeLift(base: FabRect, obstacles: FabRect[], maxLift: number, step: number, gapX = GAP) {
  for (let lift = 0; lift <= maxLift; lift += step) {
    const r = { ...base, top: base.top - lift, bottom: base.bottom - lift };
    if (!obstacles.some((o) => hits(r, o, gapX))) return lift;
  }
  return null;
}

/** Elige dónde va la burbuja: en su esquina si está libre; si no, la sube
 *  por el borde derecho hasta un hueco; si no hay hueco, se encoge a una
 *  pestaña pegada al borde (que también busca hueco). Función pura. */
export function pickFabPlacement(
  base: FabRect,
  obstacles: FabRect[],
  viewport: { width: number; height: number },
  step = 8,
): FabPlacement {
  const maxLift = Math.max(0, Math.round(viewport.height * 0.45));
  const relevant = obstacles.filter(
    (o) => o.bottom > 0 && o.top < viewport.height && o.right > 0 && o.left < viewport.width,
  );
  const lift = freeLift(base, relevant, maxLift, step);
  if (lift !== null) return { lift, tab: false };
  const tab: FabRect = {
    bottom: base.bottom,
    top: base.bottom - AYUDA_FAB_TAB.height,
    right: viewport.width,
    left: viewport.width - AYUDA_FAB_TAB.width,
  };
  // La pestaña vive en el margen de la página: basta con no encimarse.
  const tabLift = freeLift(tab, relevant, maxLift, step, 0);
  return { lift: tabLift ?? 0, tab: true };
}

/** Renglones de texto visibles (letra por letra, vía Range) en la franja
 *  derecha donde vive la burbuja: la burbuja tampoco tapa texto. Solo se
 *  miden nodos cuyo padre cruza la franja y la pantalla. */
export function collectTextRects(fab: Element, stripLeft: number, vh: number): FabRect[] {
  const out: FabRect[] = [];
  if (typeof document.createTreeWalker !== "function" || typeof document.createRange !== "function") return out;
  const parentHit = new Map<Element, boolean>();
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (!n.textContent || !n.textContent.trim()) continue;
    const parent = n.parentElement;
    if (!parent) continue;
    let ok = parentHit.get(parent);
    if (ok === undefined) {
      const r = parent.getBoundingClientRect();
      ok =
        r.width > 0 &&
        r.height > 0 &&
        r.right >= stripLeft &&
        r.bottom >= 0 &&
        r.top <= vh &&
        !parent.closest(".ayuda123-fab, .ayuda123-panel, script, style, noscript") &&
        parent !== fab;
      if (ok) {
        const cs = window.getComputedStyle(parent);
        ok = cs.visibility !== "hidden" && cs.opacity !== "0";
      }
      parentHit.set(parent, ok);
    }
    if (!ok) continue;
    range.selectNodeContents(n);
    for (const r of Array.from(range.getClientRects())) {
      if (!r.width || !r.height || r.right < stripLeft || r.bottom < 0 || r.top > vh) continue;
      out.push({ top: r.top, bottom: r.bottom, left: r.left, right: r.right });
    }
  }
  return out;
}

const DOCKED: FabPlacement = { lift: 0, tab: false };

/** En teléfono (≤760px) recalcula la posición de la burbuja cuando la
 *  página queda quieta (fin de scroll, cambio de tamaño, de ruta o del
 *  contenido) para que nunca quede encima de un control. */
export function useFabDodge(
  ref: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  routeKey: string,
): FabPlacement {
  const [placement, setPlacement] = useState<FabPlacement>(DOCKED);
  const normalSize = useRef({ width: 44, height: 44, right: 10 });

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      setPlacement(DOCKED);
      return;
    }
    const isPhone = () =>
      typeof window.matchMedia === "function" &&
      window.matchMedia(AYUDA_FAB_MOBILE_QUERY).matches;

    const evaluate = () => {
      const el = ref.current;
      if (!el) return;
      if (!isPhone()) {
        setPlacement((p) => (p.lift || p.tab ? DOCKED : p));
        return;
      }
      const cs = window.getComputedStyle(el);
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isTab = el.classList.contains("ayuda123-fab--edge-tab");
      if (!isTab) {
        normalSize.current = {
          width: el.offsetWidth || 44,
          height: el.offsetHeight || 44,
          right: parseFloat(cs.right) || 0,
        };
      }
      const bottomGap = parseFloat(cs.bottom) || 0;
      const { width, height, right } = normalSize.current;
      const base: FabRect = {
        bottom: vh - bottomGap,
        top: vh - bottomGap - height,
        right: vw - right,
        left: vw - right - width,
      };
      const obstacles: FabRect[] = [];
      document.querySelectorAll(AYUDA_FAB_OBSTACLES).forEach((node) => {
        if (node === el || node.closest(".ayuda123-fab, .ayuda123-panel")) return;
        const r = node.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) return;
        // Contenedores enormes (zona deslizable con tabindex) no cuentan.
        if (r.height > vh * 0.4) return;
        obstacles.push({ top: r.top, bottom: r.bottom, left: r.left, right: r.right });
      });
      // Tarjetas/renglones tocables sin ser <button>: cursor:pointer.
      const stripLeft = vw - 140;
      document.querySelectorAll(AYUDA_FAB_POINTER_CANDIDATES).forEach((node) => {
        if (node.closest(".ayuda123-fab, .ayuda123-panel")) return;
        const r = node.getBoundingClientRect();
        if (!r.width || !r.height || r.right < stripLeft || r.bottom < 0 || r.top > vh) return;
        if (r.height > vh * 0.4) return;
        if (window.getComputedStyle(node).cursor !== "pointer") return;
        obstacles.push({ top: r.top, bottom: r.bottom, left: r.left, right: r.right });
      });
      obstacles.push(...collectTextRects(el, stripLeft, vh));
      const next = pickFabPlacement(base, obstacles, { width: vw, height: vh });
      setPlacement((p) => (p.lift === next.lift && p.tab === next.tab ? p : next));
    };

    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = (ms = 150) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        evaluate();
      }, ms);
    };
    const onScroll = () => schedule(150);
    const onResize = () => schedule(150);
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onResize);
    const observer =
      typeof MutationObserver === "function"
        ? new MutationObserver(() => schedule(250))
        : null;
    observer?.observe(document.body, { childList: true, subtree: true });
    schedule(50);
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onResize);
      observer?.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [enabled, ref, routeKey]);

  return placement;
}
